/**
 * Design tokens + темизация (1.36.0).
 *
 * Что изменилось относительно прошлой версии
 * ─────────────────────────────────────────
 * Раньше `C` был статическим объектом с одной (тёмной) палитрой. Теперь в
 * приложении ДВЕ темы — «dark» (премиальная тёмная) и «light» (светлая
 * корпоративная), переключаемые в рантайме.
 *
 * Ключевое ограничение, которое определило архитектуру:
 *   во многих местах кода к токену дописывается альфа-суффикс, например
 *   `${C.acc}33` или `${C.re}55`. Поэтому значения токенов ОБЯЗАНЫ оставаться
 *   6-значными hex-строками (#RRGGBB) — тогда `#RRGGBB` + `33` = валидный
 *   8-значный hex (#RRGGBB33). Никаких rgb()/hsl() в значениях токенов.
 *
 * Как работает реактивность без переписывания 100+ файлов:
 *   `C` — это Proxy. При каждом обращении (C.bg, C.acc, …) он возвращает
 *   значение из активной палитры. Все компоненты читают `C.*` во время
 *   рендера (в кодовой базе нет ни одного обращения к `C` на module-scope —
 *   это проверено), поэтому достаточно перерендерить дерево при смене темы.
 *   Перерендер обеспечивает <ThemeRoot> (см. design-system/ThemeRoot.tsx),
 *   который подписан на useThemeStore и меняет key + data-theme на <html>.
 */

import { create } from 'zustand';

export type ThemeName = 'dark' | 'light';

// Тип одной палитры — фиксируем ключи, чтобы не потерять ни один токен.
export interface Palette {
  bg: string; surf: string; card: string; cardHi: string;
  b: string; bBright: string;
  acc: string; accS: string; accG: string;
  txt: string; sub: string; dim: string;
  gr: string; grS: string;
  am: string; amS: string;
  re: string; reS: string;
  bl: string; blS: string;
  pu: string; puS: string;
  // Легаси-ключи, на которые кое-где есть ссылки (раньше были undefined):
  err: string; ye: string;
}

// ─── Тёмная тема (премиальная) ─────────────────────────────────────────────
// Глубже и теплее прежней: больше контраста, мягкая оранжевая подсветка.
const DARK: Palette = {
  bg:      '#0A0C12',
  surf:    '#12161F',
  card:    '#141925',
  cardHi:  '#1A2030',
  b:       '#222A38',
  bBright: '#2D384A',
  acc:     '#FF6A2B',
  accS:    '#FF6A2B1F',
  accG:    '0 8px 26px -8px rgba(255,106,43,.55)',
  txt:     '#EEF3FA',
  sub:     '#6F8195',
  dim:     '#43526A',
  gr:      '#19D48A', grS: '#19D48A1F',
  am:      '#FFB43E', amS: '#FFB43E1F',
  re:      '#FF4D5E', reS: '#FF4D5E1F',
  bl:      '#4F9BFF', blS: '#4F9BFF1F',
  pu:      '#A781FF', puS: '#A781FF1F',
  err:     '#FF4D5E',
  ye:      '#FFB43E',
};

// ─── Светлая тема (корпоративная) ──────────────────────────────────────────
// Чистый SaaS: белые карточки, мягкие тинты, спокойный контраст.
const LIGHT: Palette = {
  bg:      '#F4F6FB',
  surf:    '#FFFFFF',
  card:    '#FFFFFF',
  cardHi:  '#F1F4F9',
  b:       '#E4E8F0',
  bBright: '#D2D9E6',
  acc:     '#F2542D',
  accS:    '#F2542D1A',
  accG:    '0 8px 22px -8px rgba(242,84,45,.35)',
  txt:     '#161C27',
  sub:     '#7A869A',
  dim:     '#AAB3C2',
  gr:      '#0FAE6E', grS: '#0FAE6E1F',
  am:      '#E8920A', amS: '#E8920A1F',
  re:      '#E5384A', reS: '#E5384A1A',
  bl:      '#2F7FF0', blS: '#2F7FF01A',
  pu:      '#7B5CF0', puS: '#7B5CF01A',
  err:     '#E5384A',
  ye:      '#E8920A',
};

export const PALETTES: Record<ThemeName, Palette> = { dark: DARK, light: LIGHT };

// ─── Стор темы (zustand) ───────────────────────────────────────────────────

const THEME_STORAGE_KEY = 'fs_theme';

function readInitialTheme(): ThemeName {
  if (typeof window === 'undefined') return 'dark';
  try {
    const saved = window.localStorage.getItem(THEME_STORAGE_KEY);
    if (saved === 'light' || saved === 'dark') return saved;
  } catch { /* localStorage может быть заблокирован */ }
  return 'dark';
}

interface ThemeState {
  theme: ThemeName;
  setTheme: (t: ThemeName) => void;
  toggleTheme: () => void;
}

export const useThemeStore = create<ThemeState>((set, get) => ({
  theme: readInitialTheme(),
  setTheme: (t) => {
    try { window.localStorage.setItem(THEME_STORAGE_KEY, t); } catch { /* noop */ }
    if (typeof document !== 'undefined') {
      document.documentElement.setAttribute('data-theme', t);
    }
    set({ theme: t });
  },
  toggleTheme: () => get().setTheme(get().theme === 'dark' ? 'light' : 'dark'),
}));

// На самом старте проставим data-theme, чтобы CSS (GlobalCSS) знал тему.
if (typeof document !== 'undefined') {
  document.documentElement.setAttribute('data-theme', readInitialTheme());
}

// ─── C: реактивный доступ к токенам через Proxy ────────────────────────────
// Палитра берётся НАПРЯМУЮ из текущего состояния стора в момент обращения.
// Раньше использовалась отдельная переменная activePalette, обновляемая через
// .subscribe(); это создавало гонку: подписанный на стор компонент мог
// перерендериться (и прочитать C.*) ДО того, как сработает наш subscribe и
// обновит activePalette — и поймать цвета прошлой темы (например, светлый фон
// со светлым текстом). Чтение из getState() убирает рассинхрон полностью.
export const C: Palette = new Proxy({} as Palette, {
  get(_t, key: string) {
    const palette = PALETTES[useThemeStore.getState().theme];
    return (palette as Record<string, string>)[key];
  },
}) as Palette;

export function paletteSnapshot(): Palette {
  return { ...PALETTES[useThemeStore.getState().theme] };
}

// ─── Шрифты (без изменений) ────────────────────────────────────────────────
const SYSTEM_SANS =
  "-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,'Helvetica Neue',Arial,sans-serif";
const SYSTEM_MONO =
  "ui-monospace,'SF Mono',SFMono-Regular,Menlo,Consolas,'Roboto Mono',monospace";

export const F = {
  head: SYSTEM_SANS,
  body: SYSTEM_SANS,
  mono: SYSTEM_MONO,
} as const;
